import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Input, Spin, message as antMessage } from "antd";
import {
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  LoadingOutlined,
  PictureOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Sparkles } from "lucide-react";
import DataProvider from "../../services";
import { useAppStore } from "../../store";
import { useChatHistory } from "../../hooks/useChatHistory";
import {
  formatDateSeparator,
  formatMessageTime,
  getDateKey,
  isAppointmentConfirmationMessage,
  parseAppointmentIdFromActionLink,
  formatBookingDateForDisplay,
  humanizeAssistantBookingDatesInContent,
  shouldHumanizeAssistantBookingDates,
} from "../../utils/chatHistoryUtils";
import type {
  ChatMessage as ChatMessageType,
  PhoneLinkDto,
  KobikuulChatTier,
  UsageStats,
} from "../../types/chat";
import ConsentModal from "../../components/chat/ConsentModal";
import { getAxiosErrorUserMessage } from "../../utils/apiErrorMessage";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB (matches API OCR limit)
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
];
const ACCEPT_IMAGE_ATTR = "image/jpeg,image/png,image/gif,image/webp";

const INITIAL_GREETING: ChatMessageType = {
  id: "greeting",
  role: "assistant",
  content:
    "Akwaaba! I'm Kobikuul, your health assistant. I can help with symptoms, medication guidance, prescription interpretation, herbal remedy safety, nutrition advice with local foods, and NHIS questions. I'm not a substitute for a doctor \u2014 for diagnosis or treatment, please see a healthcare provider or book a telehealth consultation. What can I help you with today?",
  timestamp: new Date().toISOString(),
};

function isSafeExternalUrl(url: string | undefined | null): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function getNetworkErrorMessage(error: unknown): string {
  return getAxiosErrorUserMessage(error);
}

const TYPEWRITER_CHARS_PER_TICK = 2;
const TYPEWRITER_TICK_MS = 24;

const APPOINTMENTS_LIST_PATH = "/main/appointments";

function appointmentDetailPath(id: string): string {
  return `/main/appointments/${id}`;
}

function MessageBubble({
  msg,
  onPhoneClick,
  onInAppNavigate,
  displayedLength,
}: {
  msg: ChatMessageType;
  onPhoneClick: (telLink: string) => void;
  onInAppNavigate: (path: string) => void;
  displayedLength?: number | null;
}) {
  const isUser = msg.role === "user";
  const isImage = msg.type === "image";
  const raw = msg.content ?? "";
  const isStreaming =
    !isUser && displayedLength != null && displayedLength < raw.length;
  const textToShow = !isUser
    ? isStreaming
      ? raw.slice(0, displayedLength!)
      : shouldHumanizeAssistantBookingDates(raw)
        ? humanizeAssistantBookingDatesInContent(raw)
        : raw
    : raw;

  const appointmentIdFromLink = msg.actionLink
    ? parseAppointmentIdFromActionLink(msg.actionLink)
    : null;
  const appointmentNavId =
    msg.bookingData?.appointmentId != null
      ? String(msg.bookingData.appointmentId)
      : appointmentIdFromLink;
  const showAppointmentCard =
    !isUser &&
    !isStreaming &&
    (msg.bookingData != null ||
      appointmentNavId != null ||
      isAppointmentConfirmationMessage(msg.content ?? ""));

  const content = useMemo(() => {
    if (!textToShow) return null;
    const lines = textToShow.split("\n");
    return lines.map((line, i) => {
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <span key={i}>
          {parts.map((part, j) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={j} className="font-semibold">
                {part.replace(/\*\*/g, "")}
              </strong>
            ) : (
              <span key={j}>{part}</span>
            ),
          )}
          {i < lines.length - 1 && <br />}
        </span>
      );
    });
  }, [textToShow]);

  return (
    <div
      className={`flex gap-3 max-w-[85%] ${isUser ? "ml-auto flex-row-reverse" : ""}`}
      role="article"
      aria-label={isUser ? "Your message" : "Kobikuul's reply"}
    >
      <div
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
          isUser ? "bg-neutral-200" : "bg-[#06509E]/10"
        }`}
        aria-hidden
      >
        {isUser ? (
          <UserOutlined className="text-neutral-600 text-base" />
        ) : (
          <RobotOutlined className="text-[#06509E] text-base" />
        )}
      </div>
      <div
        className={`rounded-xl px-4 py-3 shadow-sm ${
          isUser
            ? "bg-[#06509E] text-white"
            : "bg-white border border-neutral-200 border-l-4 border-l-[#06509E]"
        }`}
      >
        {isImage ? (
          /^(https?:|blob:|data:image)/i.test((msg.content || "").trim()) ? (
            <img
              src={msg.content}
              alt="Prescription or attachment"
              className="max-w-[220px] max-h-[220px] rounded-lg object-cover border border-neutral-100"
            />
          ) : (
            <span
              className={`text-sm ${isUser ? "text-white/90" : "text-neutral-500"}`}
            >
              Image shared
            </span>
          )
        ) : (
          <>
            <div
              className={`text-[15px] leading-relaxed whitespace-pre-wrap ${
                isUser ? "text-white" : "text-neutral-900"
              }`}
            >
              {content}
              {isStreaming && (
                <span
                  className="inline-block w-0.5 h-4 ml-0.5 bg-[#06509E] align-middle animate-pulse"
                  aria-hidden
                />
              )}
            </div>
            {!isUser && !isStreaming && (msg.phoneLinks?.length ?? 0) > 0 && (
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">
                  Quick actions
                </span>
                <div className="space-y-1.5">
                  {(msg.phoneLinks ?? []).map(
                    (pl: PhoneLinkDto, idx: number) => (
                      <button
                        key={idx}
                        type="button"
                        className="block w-full text-left rounded-lg px-3 py-2 border border-[#06509E]/30 bg-[#06509E]/5 text-[#06509E] hover:bg-[#06509E]/10 transition-colors text-sm font-medium"
                        onClick={() => onPhoneClick(pl.telLink)}
                        aria-label={`Call ${pl.label ?? pl.number}`}
                      >
                        {pl.label && (
                          <span className="block text-xs text-neutral-500 font-normal">
                            {pl.label}
                          </span>
                        )}
                        {pl.number}
                      </button>
                    ),
                  )}
                </div>
              </div>
            )}
            {!isUser &&
              !isStreaming &&
              (msg.links ?? []).filter((link) => isSafeExternalUrl(link.url))
                .length > 0 && (
                <div className="mt-3 pt-3 border-t border-neutral-100">
                  <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">
                    Resources
                  </span>
                  <div className="space-y-1.5">
                    {(msg.links ?? [])
                      .filter((link) => isSafeExternalUrl(link.url))
                      .map((link, idx) => (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg px-3 py-2 border border-neutral-200 bg-neutral-50 text-neutral-800 hover:bg-neutral-100 text-sm transition-colors"
                          aria-label={link.text ?? "Open link"}
                        >
                          {link.text ?? link.url}
                        </a>
                      ))}
                  </div>
                </div>
              )}
            {!isUser && !isStreaming && showAppointmentCard && (
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">
                  Appointment
                </span>
                <div className="rounded-lg border border-[#06509E]/20 bg-[#06509E]/5 p-3 space-y-2">
                  <div className="flex items-start gap-2.5">
                    <CheckCircleOutlined
                      className="text-[#06509E] text-lg mt-0.5 shrink-0"
                      aria-hidden
                    />
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-sm font-semibold text-neutral-900 m-0">
                        Appointment confirmed
                      </p>
                      {msg.bookingData?.doctorName ? (
                        <p className="text-sm text-neutral-700 m-0">
                          <span className="font-medium text-neutral-800">
                            Doctor:{" "}
                          </span>
                          {msg.bookingData.doctorName}
                        </p>
                      ) : null}
                      {msg.bookingData?.date ? (
                        <p className="text-sm text-neutral-700 m-0">
                          <span className="font-medium text-neutral-800">
                            Date:{" "}
                          </span>
                          {formatBookingDateForDisplay(msg.bookingData.date)}
                        </p>
                      ) : null}
                      {msg.bookingData?.time ? (
                        <p className="text-sm text-neutral-700 m-0">
                          <span className="font-medium text-neutral-800">
                            Time:{" "}
                          </span>
                          {msg.bookingData.time}
                        </p>
                      ) : null}
                      {!msg.bookingData &&
                        isAppointmentConfirmationMessage(msg.content ?? "") && (
                          <p className="text-sm text-neutral-600 m-0 leading-relaxed">
                            Open your appointments to view full details and join
                            your visit.
                          </p>
                        )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onInAppNavigate(
                        appointmentNavId
                          ? appointmentDetailPath(appointmentNavId)
                          : APPOINTMENTS_LIST_PATH,
                      )
                    }
                    className="flex w-full items-center justify-center gap-2 rounded-lg border border-[#06509E]/30 bg-[#06509E] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#053d7a] transition-colors"
                    aria-label={
                      appointmentNavId
                        ? "View appointment details"
                        : "Open appointments list"
                    }
                  >
                    <CalendarOutlined />
                    {appointmentNavId ? "View appointment" : "My appointments"}
                    <RightOutlined className="text-xs opacity-90" />
                  </button>
                </div>
              </div>
            )}
            {!isUser &&
              !isStreaming &&
              !showAppointmentCard &&
              isSafeExternalUrl(msg.actionLink) && (
                <a
                  href={msg.actionLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-[#06509E] text-white text-sm font-medium hover:bg-[#053d7a] transition-colors"
                  aria-label="View details"
                >
                  View details
                </a>
              )}
          </>
        )}
        {msg.timestamp && (
          <div
            className={`text-[11px] mt-1.5 ${isUser ? "text-white/80" : "text-neutral-400"}`}
          >
            {formatMessageTime(msg.timestamp)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function KobikuulChat() {
  const navigate = useNavigate();
  const user = useAppStore((s) => s.user);
  const setUser = useAppStore((s) => s.setUser);
  const chatId = useMemo(
    () => (user?.id != null ? String(user.id) : "default"),
    [user?.id],
  );

  const {
    messages: historyMessages,
    setMessages,
    refresh,
    isLoading: isHistoryLoading,
    historyPage,
    historyTotalPages,
    isLoadingMore,
    loadMore,
    error: historyError,
  } = useChatHistory(chatId);

  const displayMessages = useMemo(
    () => (historyMessages.length > 0 ? historyMessages : [INITIAL_GREETING]),
    [historyMessages],
  );

  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; visible: boolean } | null>(
    null,
  );
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamingLength, setStreamingLength] = useState(0);
  const streamingTargetRef = useRef(0);
  const streamingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const prevStreamingIdRef = useRef<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialCheckDone = useRef(false);
  const lastScrolledAtMessageIdRef = useRef<string | number | undefined>(
    undefined,
  );
  const clientMessageIdRef = useRef(0);
  const [chatUsageTier, setChatUsageTier] = useState<KobikuulChatTier | null>(
    null,
  );
  const [prescriptionUploadHint, setPrescriptionUploadHint] = useState<
    string | null
  >(null);
  const lastUsageNoticeRef = useRef<string | null>(null);

  useEffect(() => {
    lastUsageNoticeRef.current = null;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await DataProvider.getKobikuulUsage();
        if (cancelled || !data) return;
        const u = data as UsageStats;
        setChatUsageTier(u.tierName ?? null);
        setPrescriptionUploadHint(u.prescriptionUploadHint ?? null);
        if (u.usageNotice && u.usageNotice !== lastUsageNoticeRef.current) {
          lastUsageNoticeRef.current = u.usageNotice;
          antMessage.warning(u.usageNotice);
        }
      } catch {
        /* usage is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useLayoutEffect(() => {
    if (!listRef.current || isHistoryLoading || displayMessages.length === 0)
      return;
    const lastMessage = displayMessages[displayMessages.length - 1];
    const lastId = lastMessage?.id ?? lastMessage?.timestamp;
    if (lastScrolledAtMessageIdRef.current === lastId) return;
    lastScrolledAtMessageIdRef.current = lastId;
    const el = listRef.current;
    const scrollToEnd = () => {
      el.scrollTop = el.scrollHeight;
    };
    scrollToEnd();
    requestAnimationFrame(scrollToEnd);
  }, [displayMessages, isHistoryLoading]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      listRef.current?.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const showToast = useCallback((text: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, visible: true });
    toastTimerRef.current = setTimeout(() => {
      setToast((t) => (t ? { ...t, visible: false } : t));
    }, 3200);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (streamingMessageId == null) return;
    const target = streamingTargetRef.current;
    if (target <= 0) {
      setStreamingMessageId(null);
      return;
    }
    streamingIntervalRef.current = setInterval(() => {
      setStreamingLength((prev) => {
        const next = Math.min(prev + TYPEWRITER_CHARS_PER_TICK, target);
        if (next >= target) {
          if (streamingIntervalRef.current) {
            clearInterval(streamingIntervalRef.current);
            streamingIntervalRef.current = null;
          }
          setStreamingMessageId(null);
        }
        return next;
      });
    }, TYPEWRITER_TICK_MS);
    return () => {
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }
    };
  }, [streamingMessageId]);

  useEffect(() => {
    if (prevStreamingIdRef.current != null && streamingMessageId === null) {
      scrollToBottom();
    }
    prevStreamingIdRef.current = streamingMessageId;
  }, [streamingMessageId, scrollToBottom]);

  const clearSelectedFile = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [previewUrl]);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (chatUsageTier === "FREE") {
        antMessage.warning(
          prescriptionUploadHint ??
            "Prescription photo upload is reserved for Premium and Enterprise plans.",
        );
        e.target.value = "";
        return;
      }
      const mime = file.type?.toLowerCase();
      if (!ALLOWED_IMAGE_TYPES.includes(mime)) {
        antMessage.warning("Please choose a JPEG, PNG, GIF, or WebP image.");
        return;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        antMessage.warning("Image must be under 10 MB.");
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [previewUrl, chatUsageTier, prescriptionUploadHint],
  );

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    const hasImage = Boolean(selectedFile);
    if ((!text && !hasImage) || sending) return;

    if (user && user.privacyConsentAccepted !== true) {
      setShowConsent(true);
      return;
    }

    const now = new Date().toISOString();
    const messageToSend = text || "Please read this prescription image.";
    const addedCount = (hasImage ? 1 : 0) + (text ? 1 : 0);

    setMessages((prev) => {
      const base = prev.length > 0 ? prev : [INITIAL_GREETING];
      const next = [...base];
      if (hasImage && previewUrl) {
        next.push({
          id: `client-${++clientMessageIdRef.current}`,
          role: "user",
          content: previewUrl,
          type: "image",
          timestamp: now,
        });
      }
      if (text) {
        next.push({
          id: `client-${++clientMessageIdRef.current}`,
          role: "user",
          content: text,
          timestamp: now,
        });
      }
      return next;
    });
    setInputValue("");
    setSending(true);
    scrollToBottom();

    try {
      let payload: FormData | { message: string; userId?: string };
      if (hasImage && selectedFile) {
        const formData = new FormData();
        formData.append("message", messageToSend);
        if (user?.id) formData.append("userId", String(user.id));
        formData.append("image", selectedFile);
        payload = formData;
      } else {
        payload = {
          message: messageToSend,
          ...(user?.id && { userId: String(user.id) }),
        };
      }

      const { data } = await DataProvider.kobikuulV2(payload);
      const reply = data?.reply ?? "No response received.";
      if (
        data?.usageNotice &&
        data.usageNotice !== lastUsageNoticeRef.current
      ) {
        lastUsageNoticeRef.current = data.usageNotice;
        antMessage.warning(data.usageNotice);
      }
      const newReplyId = `client-${++clientMessageIdRef.current}`;
      setMessages((prev) => [
        ...prev,
        {
          id: newReplyId,
          role: "assistant",
          content: reply,
          phoneLinks: data?.phoneLinks,
          links: data?.links,
          actionLink: data?.actionLink,
          bookingData: data?.bookingData,
          timestamp: new Date().toISOString(),
        },
      ]);
      clearSelectedFile();
      if (streamingIntervalRef.current) {
        clearInterval(streamingIntervalRef.current);
        streamingIntervalRef.current = null;
      }
      streamingTargetRef.current = reply.length;
      setStreamingMessageId(newReplyId);
      setStreamingLength(0);
      scrollToBottom();
    } catch (err: unknown) {
      const msg = getNetworkErrorMessage(err);
      const isConsent =
        (err as { response?: { status?: number } })?.response?.status === 403 ||
        (typeof msg === "string" &&
          (msg.toLowerCase().includes("privacy consent") ||
            msg.toLowerCase().includes("consent")));
      if (isConsent) {
        setShowConsent(true);
        setMessages((prev) => prev.slice(0, -addedCount));
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `client-${++clientMessageIdRef.current}`,
            role: "assistant",
            content: msg,
            timestamp: new Date().toISOString(),
          },
        ]);
        antMessage.error(msg);
      }
    } finally {
      setSending(false);
    }
  }, [
    inputValue,
    sending,
    user,
    selectedFile,
    previewUrl,
    setMessages,
    clearSelectedFile,
    scrollToBottom,
  ]);

  const handleAcceptConsent = useCallback(async () => {
    setConsentLoading(true);
    try {
      await DataProvider.acceptPrivacyConsent();
      const { data } = await DataProvider.getProfile();
      if (data) setUser(data);
      setShowConsent(false);
      await refresh();
    } catch (err) {
      antMessage.error(getNetworkErrorMessage(err));
    } finally {
      setConsentLoading(false);
    }
  }, [setUser, refresh]);

  const handleDeclineConsent = useCallback(() => {
    setShowConsent(false);
    antMessage.info(
      "You can accept privacy consent later from profile to use the chat.",
    );
  }, []);

  useEffect(() => {
    if (!user || initialCheckDone.current) return;
    initialCheckDone.current = true;
    if (user.privacyConsentAccepted === true) refresh();
    else setShowConsent(true);
  }, [user, refresh]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (
        el.scrollTop <= 80 &&
        historyPage < historyTotalPages &&
        !isLoadingMore
      )
        loadMore();
    },
    [historyPage, historyTotalPages, isLoadingMore, loadMore],
  );

  const handlePhoneClick = useCallback((telLink: string) => {
    const url = telLink.startsWith("tel:")
      ? telLink
      : `tel:${telLink.replace(/\D/g, "")}`;
    window.open(url, "_self");
  }, []);

  return (
    <div
      className="h-full flex flex-col bg-neutral-50 rounded-lg overflow-hidden"
      role="main"
      aria-label="Kobikuul AI health assistant chat"
    >
      <header className="shrink-0 bg-white border-b border-neutral-200 px-4 py-2.5 sm:px-5 sm:py-4">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#06509E]/10 flex items-center justify-center shrink-0"
            aria-hidden
          >
            <RobotOutlined className="text-lg sm:text-xl text-[#06509E]" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-base sm:text-lg font-semibold text-neutral-900 m-0 tracking-tight">
                Kobikuul
              </h1>
              <span className="inline-flex items-center gap-1 rounded-xl bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-[#06509E]">
                <Sparkles className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
                Online
              </span>
            </div>
            <p className="hidden sm:block text-sm text-neutral-600 m-0">
              AI-powered health assistant
            </p>
          </div>
        </div>
        <p className="hidden sm:block text-xs text-neutral-500 mt-2 mb-0 border-t border-neutral-100 pt-2">
          For general health information, medication guidance, and
          prescription support. Not a substitute for professional care.
        </p>
      </header>

      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4 bg-neutral-50"
        role="log"
        aria-live="polite"
        aria-label="Chat messages"
      >
        {historyPage < historyTotalPages && (
          <div className="flex justify-center py-2">
            {isLoadingMore ? (
              <Spin
                size="small"
                className="[&_.ant-spin-dot-item]:!bg-[#06509E]"
              />
            ) : (
              <span className="text-xs text-neutral-500">
                Scroll up for older messages
              </span>
            )}
          </div>
        )}

        {isHistoryLoading && historyMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <span className="text-[#06509E]">
              <LoadingOutlined className="text-3xl mb-3" />
            </span>
            <span className="text-sm text-neutral-600">
              Loading conversation…
            </span>
          </div>
        ) : (
          displayMessages.map((msg, index) => {
            const prev = index > 0 ? displayMessages[index - 1] : null;
            const prevKey = getDateKey(prev?.timestamp);
            const currKey = getDateKey(msg.timestamp);
            const showDate = msg.timestamp && currKey && currKey !== prevKey;
            const stableKey = msg.id ?? `msg-${index}-${msg.timestamp ?? ""}`;

            return (
              <div key={stableKey}>
                {showDate && (
                  <div className="flex justify-center my-4">
                    <span className="text-xs font-medium text-neutral-500 bg-neutral-100 px-3 py-1.5 rounded-md">
                      {formatDateSeparator(msg.timestamp!)}
                    </span>
                  </div>
                )}
                <MessageBubble
                  msg={msg}
                  onPhoneClick={handlePhoneClick}
                  onInAppNavigate={(path) => navigate(path)}
                  displayedLength={
                    msg.id === streamingMessageId ? streamingLength : undefined
                  }
                />
              </div>
            );
          })
        )}

        {sending && (
          <div
            className="flex gap-3 max-w-[85%]"
            aria-busy="true"
            aria-label="Kobikuul is responding"
          >
            <div className="shrink-0 w-9 h-9 rounded-full bg-[#06509E]/10 flex items-center justify-center">
              <RobotOutlined className="text-[#06509E] text-base" />
            </div>
            <div className="rounded-xl px-4 py-3 bg-white border border-neutral-200 border-l-4 border-l-[#06509E] shadow-sm flex items-center gap-2">
              <span className="text-[#06509E]">
                <LoadingOutlined />
              </span>
              <span className="text-sm text-neutral-600">
                Kobikuul is responding…
              </span>
            </div>
          </div>
        )}
      </div>

      {historyError && (
        <div className="shrink-0 px-4 py-2.5 bg-amber-50 border-t border-amber-200/60 flex items-center justify-between gap-2">
          <span className="text-sm text-amber-800">{historyError}</span>
          <button
            type="button"
            onClick={() => refresh()}
            className="text-sm font-medium text-amber-800 hover:underline shrink-0"
            aria-label="Retry loading messages"
          >
            Retry
          </button>
        </div>
      )}

      <div className="relative shrink-0 px-4 py-3 sm:p-5 bg-white border-t border-neutral-200">
        {toast && (
          <div
            className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-3 z-20 transition-all duration-300 ease-out ${
              toast.visible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2 pointer-events-none"
            }`}
          >
            <button
              type="button"
              onClick={() => setToast((t) => (t ? { ...t, visible: false } : t))}
              className="flex items-center gap-2.5 max-w-[88vw] sm:max-w-sm px-4 py-3 rounded-2xl bg-white shadow-lg border border-[#06509E]/15 text-left"
            >
              <span
                className="text-xl shrink-0 h-9 w-9 rounded-full bg-sky-50 flex items-center justify-center"
                aria-hidden
              >
                📸
              </span>
              <p className="text-sm text-neutral-700 leading-snug m-0">
                {toast.text}
              </p>
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_IMAGE_ATTR}
          className="hidden"
          onChange={handleFileSelect}
          aria-label="Upload prescription image"
        />
        {selectedFile && previewUrl && (
          <div className="flex items-center gap-3 mb-3 p-3 rounded-xl bg-neutral-50 border border-neutral-200">
            <img
              src={previewUrl}
              alt="Prescription preview"
              className="w-14 h-14 rounded-lg object-cover shrink-0 border border-neutral-100"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-800 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-neutral-500">
                {(selectedFile.size / 1024).toFixed(1)} KB · prescription
                image
              </p>
            </div>
            <button
              type="button"
              onClick={clearSelectedFile}
              disabled={sending}
              className="p-2 rounded-lg text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 transition-colors shrink-0"
              aria-label="Remove attached image"
            >
              <CloseOutlined />
            </button>
          </div>
        )}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() =>
              showToast(
                "Image upload isn't available just yet. Please describe your prescription or question in the message box and Kobikuul can still help.",
              )
            }
            className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg border border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed transition-colors"
            aria-label="Attach prescription image (unavailable)"
            title="Image upload coming soon"
          >
            <PictureOutlined className="text-base" />
          </button>
          <Input.TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Ask about health, medications, or attach a prescription…"
            autoSize={{ minRows: 1, maxRows: 4 }}
            className="flex-1 rounded-lg border-neutral-300 !resize-none"
            disabled={sending}
            maxLength={2000}
            aria-label="Message input"
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={(!inputValue.trim() && !selectedFile) || sending}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 h-10 min-w-[44px] px-4 rounded-lg bg-[#06509E] text-white text-sm font-medium hover:bg-[#053d7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
          >
            {sending ? (
              <span className="text-white">
                <LoadingOutlined />
              </span>
            ) : (
              <>
                <SendOutlined className="text-sm" />
                <span className="hidden sm:inline">Send</span>
              </>
            )}
          </button>
        </div>
        <div className="flex items-start justify-between gap-3 mt-1.5 sm:mt-2.5">
          <p className="sm:hidden text-[11px] text-neutral-400 mb-0 leading-snug">
            General info only, not medical advice.
          </p>
          <p className="hidden sm:block text-xs text-neutral-500 mb-0 leading-relaxed">
            Kobikuul provides general health information only. You may
            attach a prescription image for interpretation. For diagnosis
            or treatment, please consult a healthcare provider.
          </p>
          <span className="text-[11px] text-neutral-400 shrink-0 tabular-nums mt-0.5">
            {inputValue.length}/2000
          </span>
        </div>
      </div>

      <ConsentModal
        open={showConsent}
        onAccept={handleAcceptConsent}
        onDecline={handleDeclineConsent}
        loading={consentLoading}
      />
    </div>
  );
}
